/**
 * Pricing → Tareas de Scraping (`/pricing/scraping`).
 *
 * Puerto de `Pricing::scraping_tareas` + `views/pricing/scraping_tareas.php`:
 * el lanzador de consultas al portal *Facilito* (departamento, provincia y
 * distrito desde el catálogo de ubigeo), el progreso de la cola del bot y el
 * histórico de tareas con su duración y el borrado de una tarea y sus ficheros.
 *
 * El bot hace la consulta de verdad contra Osinergmin: cada tarea abre una
 * sesión de navegador y tarda del orden de minutos, así que la pantalla deja
 * lanzarla, seguirla y cancelarla, y el progreso se refresca solo mientras la
 * cola tenga trabajo (cada 5 s).
 */

import {
  ArrowLeft,
  CircleCheck,
  CircleX,
  Clock,
  Eye,
  FileSpreadsheet,
  History,
  Loader2,
  Play,
  RefreshCw,
  Search,
  Trash2,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert, ICONO_EN_ALERTA } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/dialog';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { descargarArchivoAutenticado } from '@/lib/descargas';
import { ApiError } from '@/lib/http/api-error';
import { cargarUbigeo, distritosDe, provinciasDe, type OpcionUbigeo } from '@/lib/ubigeo';
import { cn, formatNumber } from '@/lib/utils';

import { DetalleTareaDialog } from '../components/detalle-tarea-dialog';
import {
  pricingApi,
  useCancelarTarea,
  useIniciarScraping,
  useProgresoPricing,
  useTareasPricing,
} from '../pricing.api';
import type { TareaPricing } from '../pricing.types';

const CONTROL =
  'h-11 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

/** Etiqueta y color de cada estado de tarea. */
const ESTADOS: Record<
  string,
  { etiqueta: string; variante: 'success' | 'warning' | 'danger' | 'brand' | 'outline' }
> = {
  completed: { etiqueta: 'Completada', variante: 'success' },
  running: { etiqueta: 'Ejecutando', variante: 'brand' },
  pending: { etiqueta: 'En cola', variante: 'warning' },
  failed: { etiqueta: 'Falló', variante: 'danger' },
  cancelled: { etiqueta: 'Cancelada', variante: 'outline' },
};

/** Mensaje legible de un error (de la API o de red). */
function mensajeDe(error: unknown, porDefecto: string): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;

  return porDefecto;
}

/** Fecha y hora legibles de una marca ISO. */
function fechaHora(valor?: string | null): string {
  if (!valor) return '—';

  const fecha = new Date(valor);

  if (Number.isNaN(fecha.getTime())) return valor;

  return fecha.toLocaleString('es-PE', { dateStyle: 'short', timeStyle: 'short' });
}

/** Duración legible entre dos marcas ISO. */
function duracion(tarea: TareaPricing): string {
  if (!tarea.createdAt || !tarea.completedAt) return '—';

  const segundos = Math.round(
    (new Date(tarea.completedAt).getTime() - new Date(tarea.createdAt).getTime()) / 1000,
  );

  if (!Number.isFinite(segundos) || segundos < 0) return '—';
  if (segundos < 60) return `${segundos} s`;

  const minutos = Math.floor(segundos / 60);

  return `${minutos} min ${segundos % 60} s`;
}

/** Ubicación legible de los parámetros de una tarea. */
function ubicacionDe(tarea: TareaPricing): string {
  const { departamento, provincia, distrito } = tarea.params ?? {};
  const partes = [departamento, provincia, distrito].filter(Boolean);

  return partes.length ? partes.join(' / ') : 'Todas las ubicaciones';
}

/** Nombre de la opción elegida (la consulta recibe nombres, no códigos de ubigeo). */
function nombreUbigeo(lista: OpcionUbigeo[], id: string): string {
  return lista.find((opcion) => opcion.id === id)?.name ?? '';
}

export function PricingScrapingPage() {
  useDocumentTitle('Pricing · Consultas');

  const [departamentoId, setDepartamentoId] = useState('');
  const [provinciaId, setProvinciaId] = useState('');
  const [distritoId, setDistritoId] = useState('');
  const [departamentos, setDepartamentos] = useState<OpcionUbigeo[]>([]);
  const [provincias, setProvincias] = useState<OpcionUbigeo[]>([]);
  const [distritos, setDistritos] = useState<OpcionUbigeo[]>([]);
  const [errorUbigeo, setErrorUbigeo] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [errorExportar, setErrorExportar] = useState<string | null>(null);
  const [exportando, setExportando] = useState<string | null>(null);
  const [detalle, setDetalle] = useState<TareaPricing | null>(null);
  const [aCancelar, setACancelar] = useState<TareaPricing | null>(null);

  const progreso = useProgresoPricing();
  const tareas = useTareasPricing(15);
  const iniciar = useIniciarScraping();
  const cancelar = useCancelarTarea();

  // Departamentos al abrir la pantalla; provincias y distritos en cascada.
  useEffect(() => {
    let vigente = true;

    cargarUbigeo()
      .then((datos) => {
        if (vigente) {
          setDepartamentos(datos.departamentos);
          setErrorUbigeo(null);
        }
      })
      .catch((error: unknown) => {
        if (vigente) setErrorUbigeo(mensajeDe(error, 'No se pudo cargar el catálogo de ubigeo.'));
      });

    return () => {
      vigente = false;
    };
  }, []);

  async function elegirDepartamento(id: string) {
    setDepartamentoId(id);
    setProvinciaId('');
    setDistritoId('');
    setProvincias([]);
    setDistritos([]);

    if (!id) return;

    try {
      setProvincias(await provinciasDe(id));
    } catch {
      setProvincias([]);
    }
  }

  async function elegirProvincia(id: string) {
    setProvinciaId(id);
    setDistritoId('');
    setDistritos([]);

    if (!id) return;

    try {
      setDistritos(await distritosDe(id));
    } catch {
      setDistritos([]);
    }
  }

  function limpiarUbigeo() {
    setDepartamentoId('');
    setProvinciaId('');
    setDistritoId('');
    setProvincias([]);
    setDistritos([]);
  }

  /** Descarga el Excel con los resultados de una tarea completada. */
  async function exportar(tarea: TareaPricing) {
    setErrorExportar(null);
    setExportando(tarea.id);

    try {
      await descargarArchivoAutenticado(
        pricingApi.exportarTarea(tarea.id),
        `Resultados-${ubicacionDe(tarea).replace(/[^A-Za-z0-9]+/g, '_')}.xlsx`,
      );
    } catch (fallo) {
      setErrorExportar(
        mensajeDe(fallo, 'No se pudieron descargar los resultados de la tarea.'),
      );
    } finally {
      setExportando(null);
    }
  }

  const stats = progreso.data?.stats;
  const enCurso = (stats?.pending ?? 0) + (stats?.running ?? 0);

  const cabecera = (
    <header className="flex flex-wrap items-start justify-between gap-4">
      <div>
        <Link
          to="/inicio/pricing"
          className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-ink-400 transition-colors hover:text-brand-600"
        >
          <ArrowLeft className="size-3" aria-hidden="true" />
          Pricing
        </Link>
        <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-ink-900">
          <Search className="size-6 text-brand-500" aria-hidden="true" />
          Consultas de precios
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-ink-500">
          Lanza consultas de precios contra el portal Facilito de Osinergmin, sigue su progreso y
          revisa el histórico.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Badge variant={enCurso > 0 ? 'brand' : 'outline'}>
          {enCurso > 0 ? `${formatNumber(enCurso)} en curso` : 'Cola vacía'}
        </Badge>
        {stats ? (
          <Badge variant="outline">{formatNumber(stats.total)} consultas registradas</Badge>
        ) : null}
      </div>
    </header>
  );

  if (progreso.error && !progreso.data) {
    return (
      <div className="space-y-6">
        {cabecera}
        <Alert variant="warning" title="Servicio de precios no disponible">
          <span className={ICONO_EN_ALERTA}>
            <CircleX className="size-4" aria-hidden="true" />
            {mensajeDe(progreso.error, 'No se pudo leer la cola de consultas del servicio.')}
          </span>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {cabecera}

      <Alert variant="info" title="Cada consulta recorre las estaciones">
        Una consulta recorre las estaciones de la ubicación pedida y tarda del orden de minutos. El
        progreso se refresca solo mientras haya consultas en cola, y cancelar una consulta borra
        también los datos que hubiera generado. Cuando termina, sus resultados se pueden descargar
        en Excel.
      </Alert>

      {aviso ? (
        <Alert variant="success" title="Listo">
          <span className={ICONO_EN_ALERTA}>
            <CircleCheck className="size-4" aria-hidden="true" />
            {aviso}
          </span>
        </Alert>
      ) : null}

      {errorExportar ? (
        <Alert variant="warning" title="No se pudo descargar el Excel">
          <span className={ICONO_EN_ALERTA}>
            <CircleX className="size-4" aria-hidden="true" />
            {errorExportar}
          </span>
        </Alert>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-[2fr_1fr]">
        <Card>
          <CardContent className="space-y-4">
            <h2 className="flex items-center gap-2 text-sm font-semibold text-ink-900">
              <Play className="size-4 text-brand-500" aria-hidden="true" />
              Lanzar una consulta
            </h2>

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <label htmlFor="sc-departamento" className="text-sm font-medium text-ink-700">
                  Departamento
                </label>
                <Select
                  id="sc-departamento"
                  className={CONTROL}
                  value={departamentoId}
                  onChange={(evento) => void elegirDepartamento(evento.target.value)}
                  disabled={departamentos.length === 0}
                  searchable
                >
                  <option value="">Selecciona un departamento</option>
                  {departamentos.map((opcion) => (
                    <option key={opcion.id} value={opcion.id}>
                      {opcion.name}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="flex flex-col gap-1.5">
                <label htmlFor="sc-provincia" className="text-sm font-medium text-ink-700">
                  Provincia <span className="text-ink-400">(opcional)</span>
                </label>
                <Select
                  id="sc-provincia"
                  className={CONTROL}
                  value={provinciaId}
                  onChange={(evento) => void elegirProvincia(evento.target.value)}
                  disabled={!departamentoId || provincias.length === 0}
                  searchable
                >
                  <option value="">Todas las provincias</option>
                  {provincias.map((opcion) => (
                    <option key={opcion.id} value={opcion.id}>
                      {opcion.name}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="flex flex-col gap-1.5 sm:col-span-2">
                <label htmlFor="sc-distrito" className="text-sm font-medium text-ink-700">
                  Distrito <span className="text-ink-400">(opcional)</span>
                </label>
                <Select
                  id="sc-distrito"
                  className={CONTROL}
                  value={distritoId}
                  onChange={(evento) => setDistritoId(evento.target.value)}
                  disabled={!provinciaId || distritos.length === 0}
                  searchable
                >
                  <option value="">Todos los distritos</option>
                  {distritos.map((opcion) => (
                    <option key={opcion.id} value={opcion.id}>
                      {opcion.name}
                    </option>
                  ))}
                </Select>
              </div>
            </div>

            {errorUbigeo ? <p className="text-xs text-danger-500">{errorUbigeo}</p> : null}

            <div className="flex flex-wrap items-center gap-2">
              <Button
                onClick={() =>
                  iniciar.mutate(
                    {
                      departamento: nombreUbigeo(departamentos, departamentoId),
                      provincia: nombreUbigeo(provincias, provinciaId),
                      distrito: nombreUbigeo(distritos, distritoId),
                    },
                    {
                      onSuccess: () => {
                        setAviso('La consulta se envió: sigue su progreso en la cola.');
                        void progreso.refetch();
                        void tareas.refetch();
                      },
                      onError: (error) =>
                        setAviso(mensajeDe(error, 'No se pudo lanzar la consulta.')),
                    },
                  )
                }
                disabled={iniciar.isPending || !departamentoId}
              >
                {iniciar.isPending ? (
                  <Loader2 className="mr-1.5 size-4 animate-spin" aria-hidden="true" />
                ) : (
                  <Play className="mr-1.5 size-4" aria-hidden="true" />
                )}
                {iniciar.isPending ? 'Enviando…' : 'Lanzar consulta'}
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  limpiarUbigeo();
                }}
                disabled={iniciar.isPending}
              >
                Limpiar
              </Button>
              <span className="text-xs text-ink-400">
                El departamento es obligatorio; sin provincia ni distrito se recorre todo el
                departamento.
              </span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="space-y-3">
            <header className="flex items-center justify-between gap-3">
              <h2 className="flex items-center gap-2 text-sm font-semibold text-ink-900">
                <Clock className="size-4 text-brand-500" aria-hidden="true" />
                Estado de la cola
              </h2>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => void progreso.refetch()}
                disabled={progreso.isFetching}
              >
                <RefreshCw
                  className={cn('size-3.5', progreso.isFetching && 'animate-spin')}
                  aria-hidden="true"
                />
              </Button>
            </header>

            {progreso.isLoading ? (
              <Skeleton className="h-24 w-full" />
            ) : (
              <ul className="space-y-2 text-sm">
                <li className="flex items-center justify-between">
                  <span className="text-ink-500">En cola</span>
                  <span className="font-medium text-ink-900">{formatNumber(stats?.pending ?? 0)}</span>
                </li>
                <li className="flex items-center justify-between">
                  <span className="text-ink-500">Ejecutando</span>
                  <span className="font-medium text-ink-900">{formatNumber(stats?.running ?? 0)}</span>
                </li>
                <li className="flex items-center justify-between">
                  <span className="text-ink-500">Completadas</span>
                  <span className="font-medium text-ink-900">
                    {formatNumber(stats?.completed ?? 0)}
                  </span>
                </li>
                <li className="flex items-center justify-between">
                  <span className="text-ink-500">Fallidas</span>
                  <span className="font-medium text-ink-900">{formatNumber(stats?.failed ?? 0)}</span>
                </li>
              </ul>
            )}

            {progreso.data?.currentTask ? (
              <div className="rounded-control border border-brand-100 bg-brand-50/60 p-3">
                <p className="flex items-center gap-2 text-xs font-semibold text-brand-700">
                  <Loader2 className="size-3.5 animate-spin" aria-hidden="true" />
                  Tarea en curso
                </p>
                <p className="mt-1 text-xs text-ink-600">
                  {ubicacionDe(progreso.data.currentTask)} ·{' '}
                  {formatNumber(progreso.data.currentTask.progress ?? 0)}%
                </p>
                <span className="mt-2 block h-1.5 w-full overflow-hidden rounded-full bg-white">
                  <span
                    className="block h-full rounded-full bg-brand-400 transition-all"
                    style={{ width: `${Math.max(2, progreso.data.currentTask.progress ?? 0)}%` }}
                  />
                </span>
              </div>
            ) : (
              <p className="text-xs text-ink-400">No hay ninguna consulta ejecutándose ahora mismo.</p>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="space-y-3">
          <h2 className="flex items-center gap-2 text-sm font-semibold text-ink-900">
            <History className="size-4 text-brand-500" aria-hidden="true" />
            Últimas consultas
          </h2>

          {tareas.isLoading ? (
            <Skeleton className="h-48 w-full" />
          ) : (tareas.data?.tareas ?? []).length === 0 ? (
            <p className="text-sm text-ink-500">Todavía no hay consultas registradas.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-ink-100 text-xs font-semibold uppercase tracking-wider text-ink-400">
                    <th className="py-2">Estado</th>
                    <th className="py-2">Ubicación</th>
                    <th className="py-2">Inicio</th>
                    <th className="py-2 text-right">Duración</th>
                    <th className="py-2 text-right">Intentos</th>
                    <th className="py-2 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {(tareas.data?.tareas ?? []).map((tarea) => {
                    const estado = ESTADOS[tarea.status] ?? {
                      etiqueta: tarea.status,
                      variante: 'outline' as const,
                    };
                    const viva = tarea.status === 'pending' || tarea.status === 'running';

                    return (
                      <tr key={tarea.id} className="border-b border-ink-50 last:border-0">
                        <td className="py-2">
                          <Badge variant={estado.variante}>{estado.etiqueta}</Badge>
                          {tarea.error ? (
                            <span className="mt-1 block text-[11px] text-danger-500">
                              {tarea.error}
                            </span>
                          ) : null}
                        </td>
                        <td className="py-2 text-ink-700">{ubicacionDe(tarea)}</td>
                        <td className="py-2 text-ink-500">{fechaHora(tarea.createdAt)}</td>
                        <td className="py-2 text-right text-ink-500">{duracion(tarea)}</td>
                        <td className="py-2 text-right text-ink-500">
                          {formatNumber(tarea.attempts ?? 0)}
                        </td>
                        <td className="py-2">
                          <div className="flex items-center justify-end gap-1">
                            {tarea.status === 'completed' ? (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="px-2"
                                onClick={() => setDetalle(tarea)}
                                title="Ver los precios obtenidos"
                                aria-label="Ver los precios obtenidos"
                              >
                                <Eye className="size-4" aria-hidden="true" />
                              </Button>
                            ) : null}

                            {tarea.status === 'completed' ? (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="px-2"
                                onClick={() => void exportar(tarea)}
                                disabled={exportando === tarea.id}
                                title="Descargar los resultados en Excel"
                                aria-label="Descargar los resultados en Excel"
                              >
                                {exportando === tarea.id ? (
                                  <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                                ) : (
                                  <FileSpreadsheet className="size-4" aria-hidden="true" />
                                )}
                              </Button>
                            ) : null}

                            {viva || tarea.status === 'completed' || tarea.status === 'failed' ? (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="px-2 hover:text-rose-600"
                                onClick={() => setACancelar(tarea)}
                                disabled={cancelar.isPending}
                                title={viva ? 'Cancelar la consulta' : 'Eliminar la tarea'}
                                aria-label={viva ? 'Cancelar la consulta' : 'Eliminar la tarea'}
                              >
                                <Trash2 className="size-4" aria-hidden="true" />
                              </Button>
                            ) : null}

                            {!viva && tarea.status !== 'completed' && tarea.status !== 'failed' ? (
                              <span className="text-xs text-ink-300">—</span>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {detalle ? (
        <DetalleTareaDialog
          key={detalle.id}
          tarea={detalle}
          onClose={() => setDetalle(null)}
          onDescargar={() => void exportar(detalle)}
        />
      ) : null}

      {aCancelar ? (
        <ConfirmDialog
          open
          onClose={() => setACancelar(null)}
          onConfirm={() =>
            cancelar.mutate(
              { taskId: aCancelar.id },
              { onSuccess: () => setACancelar(null) },
            )
          }
          title={
            aCancelar.status === 'completed' || aCancelar.status === 'failed'
              ? '¿Eliminar la tarea?'
              : '¿Cancelar la consulta?'
          }
          description={`Se quitará «${ubicacionDe(aCancelar)}» de la cola de consultas y se borrarán los datos que hubiera dejado esa consulta.`}
          confirmLabel="Continuar"
        />
      ) : null}
    </div>
  );
}
